import MouseMode from "../../settings/MouseMode";
import PortalDocument from "../../../document/PortalDocument";
import {ViewerMode} from "../../ViewerMode";
import React, {createRef, MouseEventHandler, ReactNode, RefObject} from "react";
import {
	Field,
	FormDocument,
	MetadataFormsFormat,
	MetadataInformation,
	MetadataRectangle,
	PagePages,
	ToolboxFormsForms
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import Immutable from "../../../../shared/immutable/Immutable";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import {FormFieldSubType} from "./FormFieldSubType";
import {PortalAnnotation} from "../../../../shared/models/PortalAnnotation";
import {TextField} from "./TextField";
import UnitHelper from "../../../../shared/helper/UnitHelper";
import LengthUnit from "../../../../shared/units/LengthUnit";
import {SignatureDialogDefinition} from "../../../dialog/components/signature/SignatureDialog";
import Parameter from "../../../dialog/parameter/Parameter";
import {PortalSignature} from "../../../../shared/models/PortalSignature";
import DialogDefault from "../../../dialog/parameter/PortalDialogDefault";
import PortalDialogType from "../../../dialog/PortalDialogType";
import {PortalAddSignature} from "../../../../shared/models/PortalAddSignature";
import AnnotationRectangle from "../annotation/AnnotationRectangle";
import Translate from "../../../../shared/translation/Translate";
import {CheckboxField} from "./CheckboxField";
import {RadioButtonField} from "./RadioButtonField";
import {ComboboxField} from "./ComboboxField";
import {ListboxField} from "./ListboxField";
import DocumentSettings from "../../../document/DocumentSettings";
import MouseFunction from "../../settings/MouseFunction";
import {FormLayerProperties, FormLayerStatus} from "./FormLayerInterface";
import FormValues from "./model/FormValues";
import FormValue from "./model/FormValue";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import PortalPureComponent from "../../../../shared/components/PortalPureComponent";

export class FormLayerDefinition extends PortalPureComponent<FormLayerProperties, FormLayerStatus> {
	private formLayer: RefObject<HTMLDivElement>;
	private readonly knownFormFields: Array<FormFieldSubType> = [
		FormFieldSubType.TEXT,
		FormFieldSubType.CHECKBOX,
		FormFieldSubType.RADIOBUTTON,
		FormFieldSubType.COMBOBOX,
		FormFieldSubType.LISTBOX
	];

	constructor(props: Readonly<FormLayerProperties>) {
		super(props);

		this.formLayer = createRef<HTMLDivElement>();
	}

	private onChange(formValues: FormValues): void {
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selected: PortalDocument = documentManager.findDocument(this.props.portalDocument.get().id!);
		let formsEditParameter: Parameter<ToolboxFormsForms> = FunctionHelper.applyValue(
			this.props.formsEditFunctionParameter, DialogDefault.getParameter(PortalDialogType.EDIT_FORMS, [selected])
		);

		this.props.context.getPage().setState({
			formsEditFunctionParameter: formsEditParameter,
			formValues: Immutable.from(formValues.toJson())
		});
	}

	private renderSignatures() {
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let portalDocument: PortalDocument = documentManager.findDocument(this.props.portalDocument.get().id!);

		let signatures: Array<ReactNode> = [];

		for (let field of FunctionHelper.applyValue(
			portalDocument.metadata?.form?.field, FormDocument.getFieldDefault()
		)) {
			if (field.subType !== FormFieldSubType.SIGNATURE || !FunctionHelper.exists(field.annotation)) {
				continue;
			}

			let scale: number = UnitHelper.unitToPx(
				this.props.zoomValue / 100, LengthUnit.POINTS, this.props.portalResolution
			);
			let annotation: PortalAnnotation | undefined = PortalAnnotation.fromJson(field.annotation![0]);
			if (!FunctionHelper.exists(annotation) || !annotation!.viewable || annotation!.hidden ||
				annotation!.invisible || this.props.pageNumber !== annotation!.page) {
				continue;
			}

			let page: PagePages | undefined = portalDocument.getPage(annotation!.page);
			let boundaries: MetadataRectangle = annotation!.getBoundaries(page);
			if (boundaries.height === 0 && boundaries.width === 0) {
				continue;
			}

			let signatureClass = "field signature-field";
			let onclick: MouseEventHandler<HTMLDivElement> | undefined;

			if (!FunctionHelper.exists(field.signature) && annotation!.writable && SignatureDialogDefinition.isActive(
				[portalDocument], this.props.context.info, this.props.signatureEditProfile
			)) {
				signatureClass += " editable";

				onclick = () => {
					let signatureParameter: Parameter<PortalSignature> = DialogDefault.getParameter(
						PortalDialogType.SIGNATURE, [portalDocument]
					);
					let addParameter: PortalAddSignature =
						signatureParameter.data.add as PortalAddSignature;
					addParameter.fieldName = FunctionHelper.applyValue(field.name, "");
					addParameter.appearance!.page = field.annotation![0].page;
					addParameter.visible = true;
					signatureParameter.data.add = addParameter;

					this.props.context.getPage().setState({
						signatureParameter: signatureParameter,
						showSignatureDialog: true
					});
				};

				if (this.props.viewerMode === ViewerMode.DEFAULT) {
					signatures.push(
						<AnnotationRectangle {...this.props} y={boundaries.y! * scale} x={0} key={signatures.length}
											 style={{transform: "scale(" + scale + ") translateX(-100%)"}}
											 className={"signature-marker"}>
							<Translate {...this.props} data="viewerscreen.signature.here" namespace="screen"/>
						</AnnotationRectangle>
					);
				}
			}

			signatures.push(
				<AnnotationRectangle {...this.props} {...boundaries} key={signatures.length} onClick={onclick}
									 zoomValue={UnitHelper.unitToPx(
										 this.props.zoomValue, LengthUnit.POINTS, this.props.portalResolution
									 )}
									 className={signatureClass}
									 style={{
										 zIndex: 0,
										 cursor: "pointer"
									 }}>
					{!FunctionHelper.exists(field.signature) &&
						<span className="icon icon-signature small"/>
					}
				</AnnotationRectangle>
			);
		}

		return signatures;
	}

	private renderFormFields(): Array<ReactNode> {
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let portalDocument: PortalDocument = documentManager.findDocument(this.props.portalDocument.get().id!);
		let formValues: FormValues = FormValues.fromJson(
			FunctionHelper.applyValue(this.props.formValues?.get(), {})
		);

		let formFields: Array<ReactNode> = [];
		for (let field of FunctionHelper.applyValue(
			portalDocument.metadata?.form?.field, FormDocument.getFieldDefault()
		)) {
			if (!this.knownFormFields.includes(field.subType) || !FunctionHelper.exists(field.annotation)) {
				continue;
			}

			let formValue: FormValue = FunctionHelper.applyValue(
				formValues.getValue(field.fullyQualifiedName),
				FormValue.fromJson({
					name: field.name,
					type: field.subType,
					value: FormValue.getValueDefault(field.subType, field.content),
					fullyQualifiedName: field.fullyQualifiedName,
					isRichtext: FunctionHelper.applyValue(field.isRichText, Field.getIsRichTextDefault())
				} as FormValue)
			);

			let formType: MetadataFormsFormat = FunctionHelper.applyValue(
				portalDocument.metadata?.information.formType, MetadataInformation.getFormTypeDefault()
			);

			switch (field.subType) {
				case FormFieldSubType.TEXT:
					formFields.push(
						<TextField {...this.props} field={Immutable.from(field.toJson())} key={field.objectKey}
								   disabled={field.readOnly} value={formValue.value}
								   onValue={(value) => {
									   formValue.value = value;
									   formValues.setValue(formValue);
									   this.onChange(formValues);
								   }}/>
					);
					break;
				case FormFieldSubType.CHECKBOX:
					formFields.push(
						<CheckboxField {...this.props} field={Immutable.from(field.toJson())} key={field.objectKey}
									   disabled={field.readOnly} value={formValue.value} checked={false}
									   onValue={(value) => {
										   formValue.value = value;
										   formValues.setValue(formValue);
										   this.onChange(formValues);
									   }}/>
					);
					break;
				case FormFieldSubType.RADIOBUTTON:
					formFields.push(
						<RadioButtonField {...this.props} field={Immutable.from(field.toJson())} key={field.objectKey}
										  className={formType === MetadataFormsFormat.StaticXFA ? "xfa-field" : ""}
										  disabled={field.readOnly} value={formValue.value}
										  onValue={(value) => {
											  formValue.value = value;
											  formValues.setValue(formValue);
											  this.onChange(formValues);
										  }}/>
					);
					break;
				case FormFieldSubType.COMBOBOX:
					formFields.push(
						<ComboboxField {...this.props} field={Immutable.from(field.toJson())} key={field.objectKey}
									   disabled={field.readOnly} value={formValue.value}
									   onValue={(value) => {
										   formValue.value = value;
										   formValues.setValue(formValue);
										   this.onChange(formValues);
									   }}/>
					);
					break;
				case FormFieldSubType.LISTBOX:
					formFields.push(
						<ListboxField {...this.props} field={Immutable.from(field.toJson())} key={field.objectKey}
									  disabled={field.readOnly} value={formValue.value}
									  onValue={(value) => {
										  formValue.value = value;
										  formValues.setValue(formValue);
										  this.onChange(formValues);
									  }}/>
					);
					break;
			}
		}

		return formFields;
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return null;
		}

		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let portalDocument: PortalDocument = documentManager.findDocument(this.props.portalDocument!.get().id!);
		let documentSettings: DocumentSettings = portalDocument.getSettings();

		let formLayerClasses: Array<string> = ["form-layer"];

		if (this.props.mouseMode === MouseMode.FORM &&
			documentSettings.viewerSettings.mouseFunction === MouseFunction.FORMS_EDIT && (
				this.props.viewerMode === ViewerMode.DEFAULT || this.props.viewerMode === ViewerMode.PORTFOLIO
			)) {
			formLayerClasses.push("selectable");
		}

		let page: PagePages | undefined = portalDocument.getPage(documentSettings.activeViewer.currentPage);
		let scale: number = documentSettings.activeViewer.zoomValue / 100;
		let pageWidth: number = FunctionHelper.applyValue(
			page?.boxes?.cropBox?.width, page?.width, MetadataRectangle.getWidthDefault()
		);
		pageWidth = UnitHelper.unitToPx(pageWidth, LengthUnit.POINTS, this.props.portalResolution) * scale;
		let pageHeight: number = FunctionHelper.applyValue(
			page?.boxes?.cropBox?.height, page?.height, MetadataRectangle.getHeightDefault()
		);
		pageHeight = UnitHelper.unitToPx(pageHeight, LengthUnit.POINTS, this.props.portalResolution) * scale;
		let xPos: number = FunctionHelper.applyValue(page?.boxes?.cropBox?.x, MetadataRectangle.getXDefault());
		xPos = UnitHelper.unitToPx(xPos, LengthUnit.POINTS, this.props.portalResolution) * scale;
		let yPos: number = FunctionHelper.applyValue(page?.boxes?.cropBox?.y, MetadataRectangle.getYDefault());
		yPos = UnitHelper.unitToPx(yPos, LengthUnit.POINTS, this.props.portalResolution) * scale;

		return (
			<div className={formLayerClasses.join(" ")} ref={this.formLayer}
				 style={{
					 maxWidth: pageWidth <= 0 ? "100%" : pageWidth,
					 maxHeight: pageHeight <= 0 ? "100%" : pageHeight,
					 left: -xPos,
					 top: yPos
				 }}>
				{this.renderFormFields()}
				{this.renderSignatures()}
			</div>
		);
	}
}

const FormLayer = React.memo(FormLayerDefinition,
	(prevProps: Readonly<FormLayerProperties>, nextProps: Readonly<FormLayerProperties>) => {
		if (!nextProps.portalDocument.equals(prevProps.portalDocument)) {
			return false;
		}

		if (FunctionHelper.exists(nextProps.formValues) && !nextProps.formValues!.equals(prevProps.formValues)) {
			return false;
		}

		if (nextProps.zoomValue !== prevProps.zoomValue) {
			return false;
		}

		if (nextProps.mouseMode !== prevProps.mouseMode) {
			return false;
		}

		if (nextProps.viewerMode !== prevProps.viewerMode) {
			return false;
		}

		if (nextProps.pageNumber !== prevProps.pageNumber) {
			return false;
		}

		if (nextProps.currentPage !== prevProps.currentPage) {
			return false;
		}

		if (nextProps.language !== prevProps.language) {
			return false;
		}

		if (nextProps.numberOfPages !== prevProps.numberOfPages) {
			return false;
		}

		return true;
	}
);

export default FormLayer;